import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./referral_code.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_icon.png";

interface ReferralCode {
  _id: string;
  code: string;
  name: string;
  phoneNumber: number;
  discountPrice: number;
  usageLimit: number;
  usageCount: number;
  isActive: boolean;
  createdAt: Date;
  discountUnit: string;
  influencerCommisionInPercentage: number;
  influencerAmount: number;
  __v: Number;
}

const ReferralCode = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [couponCode, setReferralCodes] = useState<any>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [updatePaymentModalVisible, setUpdatePaymentModalVisible] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [couponCodeToDelete, setReferralCodeToDelete] =
    useState<ReferralCode | null>(null);
  const [influencerOutstandingBalance, setInfluencerOutstandingBalance] =
    useState<number>(0);
  const [influencerCode, setInfluencerCode] = useState<string>("");

  const [addModalVisible, setAddModalVisible] = useState<boolean>(false); // Add ReferralCode Modal
  const [newReferralCode, setNewReferralCode] = useState({
    code: "",
    name: "",
    phoneNumber: "",
    discountPrice: "",
    usageLimit: "",
    expiresAt: "",
    discountUnit: "",
    influencerCommisionInPercentage: "",
    influencerAmount: "",
    ownerAmount: "",
  }); // New ReferralCode state
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // Fetch user details and couponCode
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllReferralCodes();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      handleLogout();
    }
  };

  const fetchAllReferralCodes = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-referral-codes`
      );
      setReferralCodes(response.data.coupons);
    } catch (error) {
      console.error("Failed to fetch couponCode:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDelete = async () => {
    if (!couponCodeToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `https://api.fitoja.in/api/delete-referral-code/${couponCodeToDelete.code}`,
        config
      );
      fetchAllReferralCodes();
      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete referral code:", error);
    }
  };

  const openDeleteModal = (couponCode: ReferralCode) => {
    setReferralCodeToDelete(couponCode);
    setDeleteModalVisible(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setReferralCodeToDelete(null);
  };

  const openUpdatePaymentModal = (
    code: string,
    influencerOutstandingBalance: number
  ) => {
    setInfluencerOutstandingBalance(influencerOutstandingBalance);
    setInfluencerCode(code);
    setUpdatePaymentModalVisible(true);
  };

  const closeUpdatePaymentModal = () => {
    setUpdatePaymentModalVisible(false);
    setPaidAmount(0);
  };

  // Add new coupon code API call
  const handleAddReferralCode = async () => {
    try {
      await axios.post(
        `https://api.fitoja.in/api/create-referral-code`,
        newReferralCode
      );
      fetchAllReferralCodes();
      closeAddModal();
    } catch (error) {
      console.error("Failed to add employee:", error);
      alert("Failed to add employee. Please try again.");
    }
  };

  const openAddModal = () => {
    setAddModalVisible(true);
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
    setNewReferralCode({
      code: "",
      name: "",
      phoneNumber: "",
      discountPrice: "",
      usageLimit: "",
      expiresAt: "",
      discountUnit: "",
      influencerCommisionInPercentage: "",
      influencerAmount: "",
      ownerAmount: "",
    });
  };

  // Update Payment API call
  const handleUpdatePayment = async (code, influencerAmount) => {
    try {
      await axios.put(
        `http://127.0.0.1:3000/api/update-referral-code/${code}`,
        {
          influencerAmount: influencerAmount,
        }
      );
      fetchAllReferralCodes();
      closeUpdatePaymentModal();
      setPaidAmount(0);
    } catch (error) {
      console.error("Failed to add employee:", error);
      alert("Failed to add employee. Please try again.");
    }
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  return (
    <div className="coupon-code">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <h2 style={{ marginTop: "110px", marginBottom: "0px" }}>
            Referral Codes
          </h2>

          <div className="create-new-coupon-code" onClick={openAddModal}>
            <span>Create New Referral Code</span>
          </div>

          <hr></hr>

          <div className="coupon-code-list">
            {couponCode.length > 0 ? (
              couponCode.map((couponCode) => (
                <div key={couponCode._id} className="coupon-code-card">
                  <div className="coupon-code-details">
                    <p>
                      <strong>Code:</strong> <span>{couponCode?.code}</span>
                    </p>
                    <p>
                      <strong>Name:</strong> <span>{couponCode?.name}</span>
                    </p>
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      <span>{couponCode?.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Discount Unit:</strong>{" "}
                      <span>{couponCode?.discountUnit}</span>
                    </p>
                    <p>
                      <strong>Discount:</strong>{" "}
                      <span>{couponCode?.discountPrice}</span>
                    </p>
                    <p>
                      <strong>Usage Limit:</strong>{" "}
                      <span>{couponCode?.usageLimit}</span>
                    </p>
                    <p>
                      <strong>Influencer's Commision:</strong>{" "}
                      <span>
                        {couponCode?.influencerCommisionInPercentage}%
                      </span>
                    </p>
                    <p>
                      <strong>Total Amount Received:</strong>{" "}
                      <span>
                        ₹{parseFloat(couponCode.totalAmountReceived).toFixed(1)}
                      </span>
                    </p>
                    <p>
                      <strong>Influencer's Amount:</strong>{" "}
                      <span>
                        ₹{parseFloat(couponCode.influencerAmount).toFixed(1)}
                      </span>
                    </p>
                    <p>
                      <strong>Owner's Amount:</strong>{" "}
                      <span>
                        ₹{parseFloat(couponCode.ownerAmount).toFixed(1)}
                      </span>
                    </p>
                    <p>
                      <strong>Usage Count:</strong>{" "}
                      <span>{couponCode?.usageCount}</span>
                    </p>
                  </div>
                  <div
                    className="update-payment"
                    onClick={() =>
                      openUpdatePaymentModal(
                        couponCode?.code,
                        couponCode.influencerAmount
                      )
                    }
                  >
                    Update Payment
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => openDeleteModal(couponCode)}
                  >
                    <img className="img" src={deleteIcon}></img>
                  </div>
                </div>
              ))
            ) : (
              <p
                style={{
                  display: "flex",
                  height: "70vh",
                  alignItems: "center",
                }}
              >
                No Referral Code found
              </p>
            )}
          </div>

          {deleteModalVisible && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this referral code?</p>
                <span style={{ display: "flex", gap: "20px" }}>
                  <button onClick={closeDeleteModal} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="confirm-delete-button"
                  >
                    Yes, Delete
                  </button>
                </span>
              </div>
            </div>
          )}

          {updatePaymentModalVisible && (
            <div className="add-modal">
              <div className="add-modal-content">
                <form>
                  <h2>
                    Current Outstanding Balance: {influencerOutstandingBalance}
                  </h2>

                  <input
                    type="number"
                    placeholder="Enter the amount you paid recently"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(parseFloat(e.target.value))}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      console.log("Paid Amount: ", paidAmount);
                      console.log("influencerCode: ", influencerCode);

                      handleUpdatePayment(
                        influencerCode,
                        influencerOutstandingBalance - paidAmount
                      );
                    }}
                    className="coupon-code-button"
                  >
                    Update Payment
                  </button>
                  <button
                    type="button"
                    onClick={closeUpdatePaymentModal}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}

          {addModalVisible && (
            <div className="add-modal">
              <div className="add-modal-content">
                <h2>Create Referral Code</h2>
                <form>
                  <input
                    type="text"
                    placeholder="Code"
                    value={newReferralCode.code}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        code: e.target.value.toLocaleLowerCase(),
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Discount Unit"
                    value={newReferralCode.discountUnit}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        discountUnit: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Discount"
                    value={newReferralCode.discountPrice}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        discountPrice: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Name"
                    value={newReferralCode.name}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        name: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Phone Number"
                    value={newReferralCode.phoneNumber}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        phoneNumber: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Usage Limit"
                    value={newReferralCode.usageLimit}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        usageLimit: e.target.value,
                      })
                    }
                    required
                  />
                  {/* <input
                    type="text"
                    placeholder="Expiry Date (Optional)"
                    value={newReferralCode.expiresAt}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        expiresAt: e.target.value,
                      })
                    }
                    required
                  /> */}
                  <input
                    type="text"
                    placeholder="Influencer's Commission %"
                    value={newReferralCode.influencerCommisionInPercentage}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        influencerCommisionInPercentage: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Influencer's Commission Amount"
                    value={newReferralCode.influencerAmount}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        influencerAmount: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Owner's Commission Amount"
                    value={newReferralCode.ownerAmount}
                    onChange={(e) =>
                      setNewReferralCode({
                        ...newReferralCode,
                        ownerAmount: e.target.value,
                      })
                    }
                    required
                  />
                  <button
                    type="button"
                    onClick={handleAddReferralCode}
                    className="coupon-code-button"
                  >
                    Create Referral Code
                  </button>
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReferralCode;
